/** Kinematic data upload page */
import { IonCol, IonContent, IonGrid, IonPage, IonRow, IonText } from '@ionic/react';
// Components
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { ClientCard } from '@/components/Cards/ClientCard';
import { ClientOverviewHeader } from '@/components/ClientHeaders';
import { AddClientModalContent, EmptyClientsState } from '@/components/Clients';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Modal } from '@/components/Modal/Modal';
import { Toast } from '@/components/Toasts';

import UserService from '@/hooks/useClients/userService';
import { useClientSearch } from '@/hooks/useClientSearch';

import { useAuthContext } from '@/contexts/authContext';

// Hooks
import { ExportedClient } from '@/models';

// Styles
import styles from '@/pages/ORYXGOMeasurement/Clients.module.css';

export const ORYXGOClientsOverviewPage = () => {
  const user = useAuthContext().state.userState?.user;
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState<string | null>(null);

  const [clientsList, setClientsList] = useState<ExportedClient[]>([]);

  useEffect(() => {
    // const unsubscribe = UserService.getORYXGOClients(user!.uid, setClientsList);

    const unsubscribe = UserService.getORYXGOClients(user!.uid, (clients, loading, error) => {
      setClientsList(clients);
      setIsLoading(loading);
      setIsError(error);
    });
    return () => unsubscribe(); // Cleanup on unmount
  }, [user]);

  useEffect(() => {
    console.log('Clients:', clientsList);
  }, [clientsList]);

  const [searchOptions, setSearchOptions] = useState({
    includeScore: true,
    useExtendedSearch: true,
    threshold: 0.2,
    keys: ['firstName', 'lastName', 'email'],
  });

  const { onSearch, query, searchResult } = useClientSearch<ExportedClient>(searchOptions, clientsList);

  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: new Date().toISOString(),
    email: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Handle input change and reset errors when the user types
  const handleInputChange = (field: string, value: string) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' })); // Reset error message
  };

  // Validation function
  const validateForm = () => {
    const newErrors = { firstName: '', lastName: '', email: '' };
    let isValid = true;

    if (!clientData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }
    if (!clientData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }
    if (!clientData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(clientData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle Confirm button
  const handleConfirm = async () => {
    if (validateForm()) {
      setCreatingClient(true);
      console.log('Client Data:', clientData);
      try {
        await UserService.addClient(clientData);
        console.log('Client added successfully');
        setIsAddClientModalOpen(false);
        setClientData({ firstName: '', lastName: '', dateOfBirth: new Date().toISOString(), email: '' });
        setSuccess('Client added successfully');
      } catch (error: any) {
        if (error.message === 'A user with this email already exists.') {
          setErrors((prev) => ({ ...prev, email: error.message }));
        } else {
          setError(error.message);
        }
      } finally {
        setCreatingClient(false);
      }
    }
  };

  return (
    <>
      <Modal
        footer={
          <>
            <Button onClick={() => setIsAddClientModalOpen(false)} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={() => handleConfirm()} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Add new client'
        isOpen={isAddClientModalOpen}
        onDidDismiss={() => setIsAddClientModalOpen(false)}>
        <AddClientModalContent
          clientData={clientData}
          errors={errors}
          loading={creatingClient}
          onInputChange={handleInputChange}
        />
      </Modal>

      <IonPage id='main'>
        <IonContent className={styles.content} color='light' id='main'>
          <div className={styles.header}>
            <ClientOverviewHeader title={'Clients Overview'}>
              {searchResult.length !== 0 ? (
                <Button
                  leadingIcon='plus'
                  onClick={() => setIsAddClientModalOpen(true)}
                  size='medium'
                  variant='primary'>
                  Add new client
                </Button>
              ) : null}
            </ClientOverviewHeader>
            {/* //TODO: Move this to component and check alignment? */}
            <div className={styles.TableHeader}>
              <IonGrid className={styles.TableHeaderContent} fixed>
                <IonRow className='ion-align-items-center'>
                  <IonCol className='ion-no-padding' size='7' style={{ paddingRight: '24px' }}>
                    <Input
                      leadingIcon='search'
                      onIonInput={onSearch}
                      placeholder='Search name, email...'
                      value={query}
                      variant='secondary'
                    />
                  </IonCol>
                  <IonCol className={styles.Label} size='2'>
                    <Icon color='#96A6A6' name='calendar' />
                    <IonText className={styles.LabelText}>Date of Birth</IonText>
                  </IonCol>
                  <IonCol className={styles.Label} size='3' style={{ paddingRight: '24px' }}>
                    <Icon color='#96A6A6' name='clock' />
                    <IonText className={styles.LabelText}>Last recording</IonText>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>
          </div>

          <IonGrid className={styles.grid} fixed>
            {isLoading && (
              <>
                <IonRow
                  className='ion-align-items-center ion-justify-content-center ion-margin-vertical'
                  style={{ width: '100%', height: '100%' }}>
                  <CustomLoading message={'Loading Clients...'} />
                </IonRow>
              </>
            )}
            {/* Empty State */}
            {searchResult.length === 0 && (
              <>
                <IonGrid className={styles.grid} fixed>
                  <EmptyClientsState onClickEvent={setIsAddClientModalOpen} />
                </IonGrid>
              </>
            )}

            {/* Client Cards */}

            {searchResult.map((client) => (
              <ClientCard client={client} key={client.id} location='oryxgo' />
            ))}
          </IonGrid>
        </IonContent>
      </IonPage>

      <Toast isOpen={!!error} message={error} onDidDismiss={() => setError('')} type='warning' />
      <Toast isOpen={!!isError} message={isError || ''} onDidDismiss={() => setIsError(null)} type='warning' />
      <Toast isOpen={!!success} message={success} onDidDismiss={() => setSuccess('')} type='info' />
    </>
  );
};
